import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useOrganisationSubscriptionTier } from './useOrganisationSubscriptionTier';
import { callAuthenticatedFunction } from './userManagementApi';
import wingitDesign from './designSystem';

const ASSIGNABLE_MEMBER_ROLE_VALUES = [
  'member',
  'student',
  'instructor',
  'ops',
  'admin',
];

function normalizeRoleValue(role) {
  return String(role || 'member').trim().toLowerCase();
}

function isOwnerRole(role) {
  return normalizeRoleValue(role) === 'owner';
}

function isAdminRole(role) {
  return normalizeRoleValue(role) === 'admin';
}

function isAssignableMemberRole(role) {
  return ASSIGNABLE_MEMBER_ROLE_VALUES.includes(
    normalizeRoleValue(role)
  );
}

function getAssignableRoleValue(role) {
  const normalizedRole = normalizeRoleValue(role);

  if (isAssignableMemberRole(normalizedRole)) {
    return normalizedRole;
  }

  return 'member';
}

function formatRole(role) {
  const value = normalizeRoleValue(role);

  return value.charAt(0).toUpperCase() + value.slice(1);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    String(value || '').trim()
  );
}

function sortMembersByRoleAndName(rows) {
  return [...rows].sort((a, b) => {
    const roleA = String(a?.role || '').trim().toLowerCase();
    const roleB = String(b?.role || '').trim().toLowerCase();

    if (roleA !== roleB) {
      return roleA.localeCompare(roleB);
    }

    const nameA = String(a?.full_name || a?.email || '').trim();
    const nameB = String(b?.full_name || b?.email || '').trim();

    return nameA.localeCompare(nameB);
  });
}

export default function MembersPage() {
  const navigate = useNavigate();
  const styles = getStyles();

  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [formerMembers, setFormerMembers] = useState([]);
  const [organisationCode, setOrganisationCode] = useState('');

  const [loading, setLoading] = useState(true);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');

  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [roleSelections, setRoleSelections] = useState({});
  const [changingRoleMemberKey, setChangingRoleMemberKey] =
    useState('');
  const [resettingPasswordMemberKey, setResettingPasswordMemberKey] =
    useState('');
  const [reinstatingMemberKey, setReinstatingMemberKey] =
    useState('');
  const isCurrentUserOwner =
    normalizeRoleValue(currentUserProfile?.role) === 'owner';

  const normalizedMemberSearch = memberSearch
    .trim()
    .toLowerCase();
  const filteredMembers = members.filter((member) => {
    if (!normalizedMemberSearch) {
      return true;
    }

    const fullName = String(member?.full_name || '').toLowerCase();
    const email = String(member?.email || '').toLowerCase();

    return (
      fullName.includes(normalizedMemberSearch) ||
      email.includes(normalizedMemberSearch)
    );
  });

  const {
    selectedOrganisationSubscriptionTier,
    organisationTrialActive,
  } = useOrganisationSubscriptionTier(authUser);

  const basePriceByTier = {
    calendar: 0,
    'calendar-logbook-techlog': 19.99,
    'full-package': 29.99,
  };

  const activeMemberCount = members.filter(
    (member) =>
      member.membership_status === 'active' &&
      member.is_active !== false
  ).length;

  const additionalMemberCost =
    Math.max(0, activeMemberCount - 20) * 0.5;

  const normalMonthlyCost =
    (basePriceByTier[selectedOrganisationSubscriptionTier] ?? 0) +
    additionalMemberCost;

  useEffect(() => {
    loadMembers();
  }, []);

  useEffect(() => {
    if (!inviteOpen) return undefined;

    function handleEscape(event) {
      if (event.key === 'Escape' && !sendingInvite) {
        setInviteOpen(false);
        setInviteEmail('');
        setPersonalMessage('');
        setError('');
      }
    }

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [inviteOpen, sendingInvite]);

  async function loadMembers() {
    setLoading(true);
    setError('');
    setStatus('');

    try {
      const { data: authData, error: authError } =
        await supabase.auth.getUser();

      if (authError) throw authError;

      const authUser = authData?.user;
      setAuthUser(authUser || null);

      if (!authUser?.email) {
        throw new Error('You must be logged in to view members.');
      }

      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select(
          'id, email, full_name, role, org_name, organisation_id, organisation_code, is_active, membership_status'
        )
        .eq('email', authUser.email)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profile?.organisation_id) {
        throw new Error(
          'Your account is not linked to an organisation yet.'
        );
      }

      setCurrentUserProfile(profile);

      const { data: memberRows, error: membersError } = await supabase
        .from('users')
        .select(
          'id, email, full_name, role, org_name, organisation_id, organisation_code, is_active, membership_status'
        )
        .eq('organisation_id', profile.organisation_id)
        .in('membership_status', ['active', 'suspended'])
        .order('role', { ascending: true })
        .order('full_name', { ascending: true });

      if (membersError) throw membersError;

      const { data: formerRows, error: formerError } = await supabase
        .from('users')
        .select(
          'id, email, full_name, role, org_name, organisation_id, organisation_code, is_active, membership_status'
        )
        .eq('organisation_id', profile.organisation_id)
        .eq('membership_status', 'removed')
        .order('full_name', { ascending: true });

      if (formerError) throw formerError;

      const loadedMembers = sortMembersByRoleAndName(
        memberRows || []
      );
      const loadedFormerMembers = (formerRows || []).filter(
        (member) =>
          String(member?.role || '').trim().toLowerCase() !==
          'owner'
      );

      setMembers(loadedMembers);
      setFormerMembers(loadedFormerMembers);

      const ownerRecord = loadedMembers.find(
        (member) =>
          String(member.role || '').trim().toLowerCase() === 'owner'
      );

      setOrganisationCode(
        ownerRecord?.organisation_code ||
          profile.organisation_code ||
          ''
      );
    } catch (err) {
      setError(err.message || 'Could not load members.');
    } finally {
      setLoading(false);
    }
  }




  function closeInvite() {
    if (sendingInvite) return;

    setInviteOpen(false);
    setInviteEmail('');
    setPersonalMessage('');
    setError('');
  }

  async function sendInvitation() {
    const recipientEmail = inviteEmail.trim().toLowerCase();

    setError('');
    setStatus('');

    if (!isValidEmail(recipientEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!organisationCode) {
      setError('The organisation code is not available.');
      return;
    }

    setSendingInvite(true);

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) throw sessionError;

      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error(
          'Your login session has expired. Please log in again.'
        );
      }

      const response = await fetch(
        '/.netlify/functions/send-member-invite',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            recipientEmail,
            organisationCode,
            personalMessage: personalMessage.trim(),
          }),
        }
      );

      let result = {};

      try {
        result = await response.json();
      } catch {
        result = {};
      }

      if (!response.ok) {
        throw new Error(
          result?.error ||
            'The invitation email could not be sent.'
        );
      }

      setInviteOpen(false);
      setInviteEmail('');
      setPersonalMessage('');
      setStatus(`Invitation sent to ${recipientEmail}.`);
    } catch (err) {
      setError(
        err.message || 'The invitation email could not be sent.'
      );
    } finally {
      setSendingInvite(false);
    }
  }
function updateRoleSelection(member, nextRole) {
  const memberKey = member.id || member.email;

  setRoleSelections((previous) => ({
    ...previous,
    [memberKey]: normalizeRoleValue(nextRole),
  }));
}

async function applyMemberRole(member) {
  if (!isCurrentUserOwner) {
    setError('Only the organisation owner can change member roles.');
    return;
  }

  if (isOwnerRole(member.role)) {
    setError('The Owner role cannot be changed.');
    return;
  }

  if (
    String(member?.id || '') ===
      String(currentUserProfile?.id || '') &&
    isOwnerRole(currentUserProfile?.role)
  ) {
    setError('The organisation owner cannot change their own role.');
    return;
  }

  setError('');
  setStatus('');

  const memberKey = member.id || member.email;
  const memberName = String(
    member?.full_name || member?.email || 'This member'
  ).trim();
  const currentRole = normalizeRoleValue(member.role);
  const newRole = getAssignableRoleValue(
    roleSelections[memberKey] || member.role
  );

  if (!isAssignableMemberRole(newRole)) {
    setError('Please choose a valid role.');
    return;
  }

  if (currentRole === newRole) {
    setStatus(`${memberName} is already ${formatRole(newRole)}.`);
    return;
  }

  if (isAdminRole(currentRole) || isAdminRole(newRole)) {
    const confirmationMessage = isAdminRole(newRole)
      ? `Grant Admin access to ${memberName}?`
      : `Remove Admin access from ${memberName} and change their role to ${formatRole(
          newRole
        )}?`;

    if (!window.confirm(confirmationMessage)) {
      return;
    }
  }

  setChangingRoleMemberKey(memberKey);

  try {
    await callAuthenticatedFunction(
      'manage-organisation-member',
      { action: 'change_role', userId: member.id, role: newRole },
      'Unable to change member role.'
    );

    setMembers((previous) =>
      sortMembersByRoleAndName(
        previous.map((m) =>
          m.id === member.id
            ? { ...m, role: newRole }
            : m
        )
      )
    );
    setRoleSelections((previous) => ({
      ...previous,
      [memberKey]: newRole,
    }));

    setStatus(
      `${memberName} is now ${formatRole(newRole)}.`
    );
  } catch (err) {
    setError(
      err.message || 'Unable to change member role.'
    );
  } finally {
    setChangingRoleMemberKey('');
  }
}

async function changeMemberActiveStatus(member) {
  if (
    String(member.role).toLowerCase() === 'owner'
  ) {
    setError('The Owner account cannot be suspended.');
    return;
  }

  setError('');
  setStatus('');

  const isCurrentlySuspended = member.is_active === false;
  const confirmMessage = isCurrentlySuspended
    ? `Reactivate ${member.full_name}?`
    : `Suspend ${member.full_name}? They will no longer have access to the organisation.`;

  if (!window.confirm(confirmMessage)) {
    return;
  }

  try {
    const newStatus = !member.is_active;
    const newMembershipStatus = newStatus
      ? 'active'
      : 'suspended';

    await callAuthenticatedFunction(
      'manage-organisation-member',
      {
        action: 'set_status',
        userId: member.id,
        status: newMembershipStatus,
      },
      'Unable to change member status.'
    );

    setMembers((previous) =>
      previous.map((m) =>
        m.id === member.id
          ? {
              ...m,
              is_active: newStatus,
              membership_status: newMembershipStatus,
            }
          : m
      )
    );

    const statusMessage = newStatus
      ? `${member.full_name} has been reactivated.`
      : `${member.full_name} has been suspended.`;

    setStatus(statusMessage);
  } catch (err) {
    setError(
      err.message || 'Unable to change member status.'
    );
  }
}

async function resetMemberPassword(member) {
  const memberEmail = String(member?.email || '').trim();

  if (!memberEmail) {
    setError('This member does not have an email address.');
    return;
  }

  const confirmed = window.confirm(
    `Send a password reset email to ${memberEmail}?`
  );

  if (!confirmed) {
    return;
  }

  const memberKey = member.id || member.email;

  setError('');
  setStatus('');
  setResettingPasswordMemberKey(memberKey);

  try {
    const { error: resetError } =
      await supabase.auth.resetPasswordForEmail(memberEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

    if (resetError) throw resetError;

    setStatus(
      `Password reset email sent to ${memberEmail}.`
    );
  } catch (err) {
    setError(
      err.message ||
        'Unable to send password reset email.'
    );
  } finally {
    setResettingPasswordMemberKey('');
  }
}

async function removeMemberFromOrganisation(member) {
  const roleValue = String(member?.role || '').toLowerCase();

  if (roleValue === 'owner') {
    setError('The Owner account cannot be removed.');
    return;
  }

  const memberName = String(
    member?.full_name || member?.email || 'this member'
  ).trim();

  const confirmed = window.confirm(
    `Remove ${memberName} from this organisation?\n\nThey will lose access immediately but their historical bookings and records will be retained.`
  );

  if (!confirmed) {
    return;
  }

  setError('');
  setStatus('');

  try {
    await callAuthenticatedFunction(
      'manage-organisation-member',
      { action: 'set_status', userId: member.id, status: 'removed' },
      'Unable to remove member from organisation.'
    );

    setMembers((previous) =>
      previous.filter((m) => m.id !== member.id)
    );

    setStatus(
      `${memberName} has been removed from this organisation.`
    );
  } catch (err) {
    setError(
      err.message ||
        'Unable to remove member from organisation.'
    );
  }
}

async function reinstateFormerMember(member) {
  const roleValue = String(member?.role || '').toLowerCase();

  if (roleValue === 'owner') {
    setError('Owner accounts cannot be reinstated from this list.');
    return;
  }

  const memberKey = member.id || member.email;
  const memberName = String(
    member?.full_name || member?.email || 'This member'
  ).trim();

  setError('');
  setStatus('');
  setReinstatingMemberKey(memberKey);

  try {
    await callAuthenticatedFunction(
      'manage-organisation-member',
      { action: 'set_status', userId: member.id, status: 'active' },
      'Unable to reinstate former member.'
    );

    const reinstatedMember = {
      ...member,
      membership_status: 'active',
      is_active: true,
    };

    setFormerMembers((previous) =>
      previous.filter((m) => m.id !== member.id)
    );

    setMembers((previous) =>
      sortMembersByRoleAndName([
        ...previous.filter((m) => m.id !== member.id),
        reinstatedMember,
      ])
    );

    setStatus(
      `${memberName} has been reinstated to current members.`
    );
  } catch (err) {
    setError(
      err.message ||
        'Unable to reinstate former member.'
    );
  } finally {
    setReinstatingMemberKey('');
  }
}
  return (
    <>
      <div style={styles.page}>
        <div style={styles.headerRow}>
          <div>
            <div style={styles.eyebrow}>MEMBERS</div>

            <h1 style={styles.title}>Manage your organisation</h1>

            <p style={styles.subtitle}>
              View your current members and invite new people to join
              your Wingit organisation.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={styles.secondaryButton}
          >
            Back to Admin
          </button>
        </div>

        {error && !inviteOpen && (
          <div style={styles.errorBox}>{error}</div>
        )}

        {status && (
          <div style={styles.successBox}>{status}</div>
        )}
<section style={styles.summaryGrid}>
  <div style={styles.summaryCard}>
    <div style={styles.summaryLabel}>Active Members</div>
    <div style={styles.summaryValue}>
      {members.length}
    </div>
  </div>

  <div style={styles.summaryCard}>
    <div style={styles.summaryLabel}>Admins</div>
    <div style={styles.summaryValue}>
      {
        members.filter(
          (m) =>
            String(m.role).toLowerCase() === 'admin' ||
            String(m.role).toLowerCase() === 'owner'
        ).length
      }
    </div>
  </div>

  <div style={styles.summaryCard}>
    <div style={styles.summaryLabel}>Monthly Cost</div>
    {organisationTrialActive ? (
      <>
        <div style={styles.summaryValue}>£0</div>
        <div style={{ ...styles.muted, fontSize: '0.72rem', marginTop: '0.2rem' }}>
          30-day Full Package trial
        </div>
        <div style={{ ...styles.muted, fontSize: '0.72rem' }}>
          Then £{normalMonthlyCost.toFixed(2)}/month
        </div>
      </>
    ) : (
      <div style={styles.summaryValue}>
        £{normalMonthlyCost.toFixed(2)}
      </div>
    )}
  </div>

  <div style={styles.summaryCard}>
    <div style={styles.summaryLabel}>Organisation Code</div>
    <div
      style={{
        ...styles.summaryValue,
        fontSize: '1rem',
        letterSpacing: '0.06em',
      }}
    >
      {organisationCode || '--'}
    </div>
  </div>
</section>

        <section style={styles.panel}>
          <div style={styles.inviteHeader}>
            <div>
              <h2 style={styles.panelTitle}>Invite Member</h2>

              <p style={styles.muted}>
                Send an email invitation to add someone to this organisation.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setError('');
                setStatus('');
                setInviteOpen(true);
              }}
              disabled={sendingInvite || !organisationCode}
              style={styles.primaryButton}
            >
              Invite Member
            </button>
          </div>

          <div style={styles.codePanel}>
            <div>
              <div style={styles.codeLabel}>Organisation code</div>

              <div style={styles.codeValue}>
                {organisationCode || '--'}
              </div>
            </div>
          </div>

          <p style={styles.codeHelp}>
            Invites include your organisation code so members can join the correct account.
          </p>
        </section>

        <section style={styles.panel}>
          <div style={styles.sectionHeader}>
            <div>
              <h2 style={styles.panelTitle}>Current members</h2>

              <p style={styles.muted}>
                {loading
                  ? 'Loading organisation members...'
                  : `${members.length} ${
                      members.length === 1 ? 'member' : 'members'
                    } in this organisation`}
              </p>
            </div>
          </div>

          <label style={styles.field}>
            <div style={styles.label}>Search members</div>

            <input
              type="search"
              value={memberSearch}
              onChange={(event) =>
                setMemberSearch(event.target.value)
              }
              placeholder="Search members by name or email"
              style={styles.input}
            />
          </label>

          {loading ? (
            <div style={styles.emptyState}>Loading members...</div>
          ) : members.length === 0 ? (
            <div style={styles.emptyState}>
              No members have joined this organisation yet.
            </div>
          ) : filteredMembers.length === 0 ? (
            <div style={styles.emptyState}>
              No members match your search.
            </div>
          ) : (
            <div style={styles.memberList}>
  {filteredMembers.map((member) => (
    <details
      key={member.id || member.email}
      style={styles.memberCard}
    >
      <summary style={styles.memberSummary}>
        <div style={styles.memberDetails}>
          <div style={styles.memberName}>
            {member.full_name || 'Unnamed member'}
          </div>

          <div style={styles.memberEmail}>
            {member.email || 'No email address'}
          </div>
        </div>

        <div style={styles.memberSummaryRight}>
          <div style={styles.roleBadge}>
            {formatRole(member.role)}
          </div>

          <span style={styles.expandIcon}>▼</span>
        </div>
      </summary>

      <div style={styles.memberExpanded}>
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Email</span>

          <span style={styles.detailValue}>
            {member.email || 'Not supplied'}
          </span>
        </div>

        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Role</span>

          <span style={styles.detailValue}>
            {formatRole(member.role)}
          </span>
        </div>

        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Organisation</span>

          <span style={styles.detailValue}>
            {member.org_name ||
              currentUserProfile?.org_name ||
              'Not available'}
          </span>
        </div>

        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Member ID</span>

          <span style={styles.detailValue}>
            {member.id || 'Not available'}
          </span>
        </div>
        {isOwnerRole(member.role) ? (
  <>
    <div style={styles.actionsDivider} />

    <div style={styles.ownerInfo}>
      <div style={styles.ownerTitle}>
  👑 Protected Owner Account
</div>

      <div style={styles.ownerText}>
  <strong>This account always retains ownership of the organisation.</strong>

  <br />
  <br />

  It cannot be suspended, removed or have its role
  changed.
</div>
    </div>
  </>
) : (
  <>
    <div style={styles.actionsDivider} />

    <div style={styles.actionsTitle}>
      Member Actions
    </div>

    <div style={styles.actionsGrid}>
      <div style={styles.roleActionGroup}>
        <select
          value={
            roleSelections[member.id || member.email] ||
            getAssignableRoleValue(member.role)
          }
          onChange={(event) =>
            updateRoleSelection(member, event.target.value)
          }
          disabled={
            !isCurrentUserOwner ||
            changingRoleMemberKey ===
              (member.id || member.email)
          }
          style={styles.compactSelect}
          aria-label={`Role for ${
            member.full_name || member.email || 'member'
          }`}
        >
          {ASSIGNABLE_MEMBER_ROLE_VALUES.map((roleValue) => (
            <option key={roleValue} value={roleValue}>
              {formatRole(roleValue)}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => applyMemberRole(member)}
          disabled={
            !isCurrentUserOwner ||
            changingRoleMemberKey ===
              (member.id || member.email) ||
            normalizeRoleValue(
              roleSelections[member.id || member.email] ||
                member.role
            ) === normalizeRoleValue(member.role)
          }
          style={styles.compactSecondaryButton}
        >
          {changingRoleMemberKey ===
          (member.id || member.email)
            ? 'Applying…'
            : 'Apply Role'}
        </button>
      </div>

      <button
        type="button"
        onClick={() => changeMemberActiveStatus(member)}
        style={styles.secondaryButton}
      >
        {member.is_active === false ? 'Reactivate' : 'Suspend'}
      </button>

      <button
        type="button"
        onClick={() => resetMemberPassword(member)}
        disabled={
          resettingPasswordMemberKey ===
          (member.id || member.email)
        }
        style={styles.secondaryButton}
      >
        {resettingPasswordMemberKey ===
        (member.id || member.email)
          ? 'Sending…'
          : 'Reset Password'}
      </button>

      <button
        type="button"
        onClick={() => removeMemberFromOrganisation(member)}
        style={styles.dangerButton}
      >
        Remove Member
      </button>
    </div>

    {!isCurrentUserOwner && (
      <div style={styles.roleHelpText}>
        Only the organisation owner can change roles.
      </div>
    )}
  </>
)}
      </div>
    </details>
  ))}
</div>
          )}
        </section>

        <section style={styles.panel}>
          <div style={styles.sectionHeader}>
            <div>
              <h2 style={styles.panelTitle}>Former Members</h2>

              <p style={styles.muted}>
                {formerMembers.length === 0
                  ? 'No former members'
                  : `${formerMembers.length} former ${
                      formerMembers.length === 1
                        ? 'member'
                        : 'members'
                    }`}
              </p>
            </div>
          </div>

          {formerMembers.length === 0 ? (
            <div style={styles.emptyState}>No former members</div>
          ) : (
            <div style={styles.memberList}>
              {formerMembers.map((member) => {
                const memberKey = member.id || member.email;
                const isReinstating =
                  reinstatingMemberKey === memberKey;

                return (
                  <div
                    key={memberKey}
                    style={styles.memberCard}
                  >
                    <div style={styles.memberSummary}>
                      <div style={styles.memberDetails}>
                        <div style={styles.memberName}>
                          {member.full_name || 'Unnamed member'}
                        </div>

                        <div style={styles.memberEmail}>
                          {member.email || 'No email address'}
                        </div>

                        <div style={styles.memberEmail}>
                          Role: {formatRole(member.role)}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          reinstateFormerMember(member)
                        }
                        disabled={isReinstating}
                        style={styles.secondaryButton}
                      >
                        {isReinstating
                          ? 'Reinstating...'
                          : 'Reinstate'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        

      {inviteOpen && (
        <div
          style={styles.modalOverlay}
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !sendingInvite
            ) {
              closeInvite();
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="invite-member-title"
            style={styles.modal}
          >
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.eyebrow}>INVITATION</div>

                <h2
                  id="invite-member-title"
                  style={styles.modalTitle}
                >
                  Invite a new member
                </h2>

                <p style={styles.modalSubtitle}>
                  Wingit will send a branded invitation containing your
                  organisation code.
                </p>
              </div>

              <button
                type="button"
                onClick={closeInvite}
                disabled={sendingInvite}
                aria-label="Close invitation"
                style={styles.closeButton}
              >
                ×
              </button>
            </div>

            {error && (
              <div style={styles.modalError}>{error}</div>
            )}

            <label style={styles.field}>
              <div style={styles.label}>Email address</div>

              <input
                type="email"
                value={inviteEmail}
                onChange={(event) => {
                  setInviteEmail(event.target.value);
                  setError('');
                }}
                placeholder="member@example.com"
                style={styles.input}
                disabled={sendingInvite}
                autoFocus
              />
            </label>

            <label style={styles.field}>
              <div style={styles.label}>
                Personal message{' '}
                <span style={styles.optionalText}>Optional</span>
              </div>

              <textarea
                value={personalMessage}
                onChange={(event) => {
                  setPersonalMessage(event.target.value);
                  setError('');
                }}
                placeholder="Add a short welcome message..."
                rows={5}
                style={styles.textarea}
                disabled={sendingInvite}
              />
            </label>

            <div style={styles.modalCode}>
              <span style={styles.modalCodeLabel}>
                Organisation code
              </span>

              <span style={styles.modalCodeValue}>
                {organisationCode}
              </span>
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={closeInvite}
                disabled={sendingInvite}
                style={styles.secondaryButton}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={sendInvitation}
                disabled={sendingInvite}
                style={{
                  ...styles.primaryButton,
                  opacity: sendingInvite ? 0.65 : 1,
                  cursor: sendingInvite
                    ? 'not-allowed'
                    : 'pointer',
                }}
              >
                {sendingInvite
                  ? 'Sending Invitation...'
                  : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

function getStyles() {
  const ds = wingitDesign;

  return {
    page: {
      maxWidth: 1040,
      margin: '2.5rem auto',
      padding: '0 1rem 2rem',
      color: ds.colors.text,
    },

    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      flexWrap: 'wrap',
      marginBottom: '1rem',
    },

    eyebrow: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 800,
      marginBottom: '0.35rem',
    },

    title: {
      ...ds.typography.pageTitle,
      color: ds.colors.text,
      margin: 0,
      fontSize: '1.7rem',
      fontWeight: 750,
      letterSpacing: '-0.03em',
    },

    subtitle: {
      ...ds.typography.body,
      color: ds.colors.textMuted,
      margin: '0.4rem 0 0',
      maxWidth: 650,
    },
summaryGrid: {
  display: 'grid',
  gridTemplateColumns:
    'repeat(auto-fit, minmax(180px,1fr))',
  gap: '1rem',
  marginTop: '1rem',
},

summaryCard: {
  ...ds.cards.secondary,
  borderRadius: ds.radius.large,
  padding: '1rem',
  textAlign: 'left',
},

summaryLabel: {
  color: ds.colors.textMuted,
  fontSize: '0.8rem',
  fontWeight: 700,
  marginBottom: '0.4rem',
},

summaryValue: {
  color: ds.colors.text,
  fontSize: '1.6rem',
  fontWeight: 800,
},
bottomGrid: {
  display: 'grid',
  gridTemplateColumns:
    'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '1rem',
  marginTop: '1rem',
},

bottomCard: {
  ...ds.cards.secondary,
  borderRadius: ds.radius.large,
  padding: '1.15rem',
  textAlign: 'left',
  boxShadow: ds.shadow.card,
},

bottomCodePanel: {
  display: 'grid',
  gap: '1rem',
  marginTop: '1rem',
  padding: '1rem',
  border: `1px solid ${ds.colors.border}`,
  borderRadius: ds.radius.medium,
  background: 'rgba(12,22,36,0.55)',
},
    panel: {
      ...ds.cards.secondary,
      borderRadius: ds.radius.large,
      padding: '1.15rem',
      marginTop: '1rem',
      textAlign: 'left',
      boxShadow: ds.shadow.card,
    },

    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      flexWrap: 'wrap',
      marginBottom: '1rem',
    },

    inviteHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap',
      marginBottom: '1rem',
    },

    panelTitle: {
      ...ds.typography.cardTitle,
      color: ds.colors.text,
      margin: 0,
      fontSize: '1.05rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },

    muted: {
      ...ds.typography.body,
      color: ds.colors.textMuted,
      margin: '0.3rem 0 0',
      fontSize: '0.9rem',
    },

    memberList: {
      display: 'grid',
      gap: '0.65rem',
    },

    memberCard: {
  border: `1px solid ${ds.colors.border}`,
  borderRadius: ds.radius.medium,
  background: 'rgba(12,22,36,0.48)',
  overflow: 'hidden',
},

memberSummary: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  padding: '0.85rem 0.9rem',
  cursor: 'pointer',
  listStyle: 'none',
},

memberSummaryRight: {
  display: 'flex',
  alignItems: 'center',
  gap: '0.7rem',
  flexShrink: 0,
},

expandIcon: {
  color: ds.colors.textMuted,
  fontSize: '0.72rem',
},

memberExpanded: {
  display: 'grid',
  gap: '0.7rem',
  padding: '0.9rem',
  borderTop: `1px solid ${ds.colors.border}`,
  background: 'rgba(8,16,28,0.32)',
},

detailRow: {
  display: 'grid',
  gridTemplateColumns: '130px minmax(0, 1fr)',
  gap: '1rem',
  alignItems: 'start',
},

detailLabel: {
  color: ds.colors.textMuted,
  fontSize: '0.82rem',
  fontWeight: 650,
},

detailValue: {
  color: ds.colors.text,
  fontSize: '0.86rem',
  overflowWrap: 'anywhere',
},

    memberDetails: {
      minWidth: 0,
    },

    memberName: {
      color: ds.colors.text,
      fontSize: '0.95rem',
      fontWeight: 680,
      lineHeight: 1.3,
    },

    memberEmail: {
      color: ds.colors.textMuted,
      fontSize: '0.84rem',
      marginTop: '0.2rem',
      overflowWrap: 'anywhere',
    },

    roleBadge: {
      color: ds.colors.textSoft,
      background: 'rgba(150,176,207,0.08)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: 999,
      padding: '0.3rem 0.58rem',
      fontSize: '0.72rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },

    codePanel: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap',
      padding: '1rem',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.medium,
      background: 'rgba(12,22,36,0.55)',
    },

    codeLabel: {
      ...ds.typography.caption,
      color: ds.colors.textMuted,
      fontWeight: 700,
      marginBottom: '0.35rem',
    },

    codeValue: {
      color: ds.colors.text,
      fontSize: '1.35rem',
      fontWeight: 720,
      letterSpacing: '0.08em',
      overflowWrap: 'anywhere',
    },

    codeActions: {
      display: 'flex',
      gap: '0.65rem',
      flexWrap: 'wrap',
    },

    codeHelp: {
      color: ds.colors.textMuted,
      fontSize: '0.8rem',
      lineHeight: 1.4,
      margin: '0.75rem 0 0',
    },

    primaryButton: {
      ...ds.buttons.primary,
      fontSize: '0.84rem',
      padding: '0.72rem 1rem',
    },

    secondaryButton: {
      ...ds.buttons.secondary,
      fontSize: '0.84rem',
      padding: '0.72rem 1rem',
    },

    emptyState: {
      color: ds.colors.textMuted,
      background: 'rgba(150,176,207,0.05)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.medium,
      padding: '1rem',
      fontSize: '0.9rem',
      lineHeight: 1.4,
    },

    errorBox: {
      ...ds.chips.danger,
      borderRadius: ds.radius.medium,
      padding: '0.8rem',
      marginTop: '1rem',
      fontSize: '0.88rem',
    },

    successBox: {
      color: '#b7ffdc',
      background: 'rgba(47,211,125,0.08)',
      border: '1px solid rgba(47,211,125,0.24)',
      borderRadius: ds.radius.medium,
      padding: '0.8rem',
      marginTop: '1rem',
      fontSize: '0.88rem',
    },
    ownerInfo: {
  marginTop: '0.75rem',
  padding: '1rem',
  borderRadius: ds.radius.medium,
  background: 'rgba(255,215,0,0.04)',
  border: '1px solid rgba(255,215,0,0.12)',
},

ownerTitle: {
  color: '#ffd76a',
  fontSize: '0.95rem',
  fontWeight: 700,
  marginBottom: '0.6rem',
},

ownerText: {
  color: ds.colors.textMuted,
  fontSize: '0.84rem',
  lineHeight: 1.6,
},
actionsDivider: {
  height: 1,
  background: ds.colors.border,
  margin: '0.25rem 0 0.75rem',
},

actionsTitle: {
  color: ds.colors.textMuted,
  fontSize: '0.82rem',
  fontWeight: 700,
},

actionsGrid: {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.6rem',
  marginTop: '0.75rem',
},

roleActionGroup: {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.45rem',
  flexWrap: 'wrap',
},

compactSelect: {
  minWidth: 118,
  padding: '0.6rem 0.72rem',
  borderRadius: ds.radius.medium,
  border: `1px solid ${ds.colors.border}`,
  background: 'rgba(12,22,36,0.72)',
  color: ds.colors.text,
  fontSize: '0.82rem',
  fontWeight: 650,
  outline: 'none',
},

compactSecondaryButton: {
  ...ds.buttons.secondary,
  fontSize: '0.82rem',
  padding: '0.6rem 0.82rem',
},

roleHelpText: {
  color: ds.colors.textMuted,
  fontSize: '0.78rem',
  marginTop: '0.45rem',
},

dangerButton: {
  ...ds.buttons.secondary,
  background: 'rgba(180,40,40,0.12)',
  border: '1px solid rgba(220,80,80,0.35)',
  color: '#ffb3b3',
  fontSize: '0.84rem',
  padding: '0.72rem 1rem',
},
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      background: 'rgba(4,10,18,0.76)',
      backdropFilter: 'blur(8px)',
    },

    modal: {
      width: '100%',
      maxWidth: 540,
      maxHeight: 'calc(100vh - 2rem)',
      overflowY: 'auto',
      background: ds.colors.surface,
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.large,
      boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
      padding: '1.2rem',
      color: ds.colors.text,
    },

    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      marginBottom: '1rem',
    },

    modalTitle: {
      margin: 0,
      color: ds.colors.text,
      fontSize: '1.25rem',
      fontWeight: 740,
      letterSpacing: '-0.025em',
    },

    modalSubtitle: {
      ...ds.typography.body,
      color: ds.colors.textMuted,
      margin: '0.35rem 0 0',
      fontSize: '0.88rem',
      lineHeight: 1.45,
    },

    closeButton: {
      background: 'transparent',
      color: ds.colors.textMuted,
      border: 'none',
      padding: '0.15rem 0.35rem',
      fontSize: '1.5rem',
      lineHeight: 1,
      cursor: 'pointer',
    },

    modalError: {
      ...ds.chips.danger,
      borderRadius: ds.radius.medium,
      padding: '0.75rem',
      marginBottom: '0.9rem',
      fontSize: '0.86rem',
    },

    field: {
      display: 'block',
      marginTop: '0.85rem',
    },

    label: {
      color: ds.colors.textMuted,
      fontSize: '0.82rem',
      fontWeight: 650,
      marginBottom: '0.35rem',
    },

    optionalText: {
      color: ds.colors.textMuted,
      fontWeight: 500,
      opacity: 0.75,
    },

    input: {
      width: '100%',
      padding: '0.7rem 0.75rem',
      borderRadius: ds.radius.medium,
      border: `1px solid ${ds.colors.border}`,
      background: 'rgba(12,22,36,0.72)',
      color: ds.colors.text,
      boxSizing: 'border-box',
      fontSize: '0.9rem',
      outline: 'none',
    },

    textarea: {
      width: '100%',
      minHeight: 116,
      resize: 'vertical',
      padding: '0.7rem 0.75rem',
      borderRadius: ds.radius.medium,
      border: `1px solid ${ds.colors.border}`,
      background: 'rgba(12,22,36,0.72)',
      color: ds.colors.text,
      boxSizing: 'border-box',
      fontSize: '0.9rem',
      fontFamily: 'inherit',
      lineHeight: 1.45,
      outline: 'none',
    },

    modalCode: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '1rem',
      padding: '0.75rem',
      background: 'rgba(150,176,207,0.06)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.medium,
    },

    modalCodeLabel: {
      color: ds.colors.textMuted,
      fontSize: '0.78rem',
    },

    modalCodeValue: {
      color: ds.colors.text,
      fontSize: '0.9rem',
      fontWeight: 720,
      letterSpacing: '0.06em',
    },

    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.65rem',
      flexWrap: 'wrap',
      marginTop: '1rem',
    },
  };
}